import { useStoredState } from '../../common/useStoredState';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DefaultTheme } from 'styled-components';

import {
  ThemeProvider as ArcThemeProvider,
  themes,
  SynthwaveGlobalStyles,
} from '@electricjs/arc';

import useThemeDetector from '@/hooks/useThemeDetector';
import { LocalStorageKeys } from '@/constants/localStorageKeys';

const isDevOrStaging =
  window._env_?.VITE_ENV_NAME === 'local' ||
  window._env_?.VITE_ENV_NAME === 'staging';

// TODO: Future Arc release: Get the enum values from the theme object names after they're exported
// https://github.com/ElectricAI/electricjs-arc/blob/a6c40635d0bc9c0743b233b431a5eb82fff45a63/src/themes/themes.ts#L11-L14
export enum Themes {
  LIGHT = 'Electric',
  AUTO = 'System',
  DARK = 'Electric Dark',
  SYNTHWAVE = 'Electric Synthwave',
}

type ThemeName = DefaultTheme['name'];

type ThemeContextProps = {
  theme: DefaultTheme;
  themeName: ThemeName;
  setThemeValue: (_: ThemeName) => void;
};

export const ThemeContext = createContext({
  theme: themes.ELECTRIC,
  themeName: themes.ELECTRIC.name,
  setThemeValue: _ => themes.ELECTRIC.name,
} as ThemeContextProps);

type ThemeProviderProps = {
  children: React.ReactNode;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { ELECTRIC, ELECTRIC_DARK } = themes;

  const [electrolyteTheme, setElectrolyteTheme] = useStoredState(
    LocalStorageKeys.ELECTROLYTE_THEME,
    ELECTRIC.name
  );

  const [themeName, setThemeName] = useState(electrolyteTheme || ELECTRIC.name);

  const setThemeValue = useCallback(
    (name: ThemeName) => {
      setThemeName(name);
      setElectrolyteTheme(name || '');
    },
    [setElectrolyteTheme]
  );

  useEffect(() => {
    setThemeName(electrolyteTheme ?? ELECTRIC.name);
  }, [electrolyteTheme, ELECTRIC.name]);

  const isDarkTheme = useThemeDetector();

  const theme = useMemo(() => {
    if (themeName === Themes.SYNTHWAVE && !isDevOrStaging) {
      return ELECTRIC;
    }
    if (themeName === Themes.AUTO) {
      return isDarkTheme ? ELECTRIC_DARK : ELECTRIC;
    } else {
      return (
        Object.values(themes).find(
          currentTheme => currentTheme.name === themeName
        ) || ELECTRIC
      );
    }
  }, [themeName, isDarkTheme, ELECTRIC_DARK, ELECTRIC]);

  const DARK_MODE_CLASS = 'dark-mode';

  useEffect(() => {
    if (theme.name === Themes.DARK || theme.name === Themes.SYNTHWAVE) {
      document.body.classList.add(DARK_MODE_CLASS);
    } else {
      document.body.classList.remove(DARK_MODE_CLASS);
    }
  }, [theme]);

  return (
    <ArcThemeProvider theme={theme}>
      {theme.name === Themes.SYNTHWAVE && <SynthwaveGlobalStyles />}
      <ThemeContext.Provider value={{ theme, themeName, setThemeValue }}>
        {children}
      </ThemeContext.Provider>
    </ArcThemeProvider>
  );
};
